import type { SbCompliancePagesParams } from "@common/api/aircraft"
import {
  periodRange,
  periodSchema,
} from "@common/components/filters/period-model"
import { z } from "zod"
import type { ColumnSizing } from "@/lib/column-widths"
import { withFixed } from "@/routes/_layout/maintenance-programmes_/$programmeId/-lib/table"

export const SB_COLUMNS = [
  "number",
  "title",
  "applicability",
  "accepted",
] as const

export const sbColumnSchema = z.enum(SB_COLUMNS)

export type SbColumn = z.infer<typeof sbColumnSchema>

export const SB_COLUMN_LABELS: Record<SbColumn, string> = {
  number: "SB no.",
  title: "Title",
  applicability: "Applicability",
  accepted: "Accepted",
}

export type SbSlot = SbColumn | "select"

export const SB_SIZING: ColumnSizing<SbSlot> = {
  id: "aircraft-sb-compliance",
  widths: {
    select: 2.25,
    number: 9,
    title: 20,
    applicability: 14,
    accepted: 7,
  },
  fill: "title",
  pinned: ["select"],
  extras: ["select"],
  dropOrder: ["applicability", "accepted"],
}

export const sbSearchFields = {
  sb_search: z.string().optional().catch(undefined),
  sb_accepted_by: z.array(z.string()).optional().catch(undefined),
  sb_period: periodSchema.optional().catch(undefined),
  sb_from: z.string().optional().catch(undefined),
  sb_to: z.string().optional().catch(undefined),
  sb_columns: z.array(sbColumnSchema).optional().catch(undefined),
  sb_item: z.string().optional().catch(undefined),
}

const sbSearchSchema = z.object(sbSearchFields)

export type SbSearch = z.infer<typeof sbSearchSchema>

export type SbFilterValues = Pick<
  SbSearch,
  "sb_accepted_by" | "sb_period" | "sb_from" | "sb_to"
>

export const NO_SB_FILTERS: SbFilterValues = {
  sb_accepted_by: undefined,
  sb_period: undefined,
  sb_from: undefined,
  sb_to: undefined,
}

export function hasSbFilters(values: SbFilterValues): boolean {
  return Boolean(values.sb_accepted_by?.length || values.sb_period)
}

export function sbSlotsOf(columns: SbColumn[]): SbSlot[] {
  return ["select", ...columns]
}

export function sbColumnsOf(search: SbSearch): SbColumn[] {
  return search.sb_columns
    ? withFixed(SB_COLUMNS, "number", search.sb_columns)
    : [...SB_COLUMNS]
}

export function sbCompliancePagesParams(
  aircraftId: string,
  search: SbSearch,
): SbCompliancePagesParams {
  const accepted = periodRange(search.sb_period, search.sb_from, search.sb_to)
  return {
    aircraft_id: aircraftId,
    search: search.sb_search,
    accepted_by_id: search.sb_accepted_by,
    accepted_from: accepted.from,
    accepted_to: accepted.to,
  }
}

type SbDetailKey =
  | "manpower_requirements"
  | "electrical_load_analysis"
  | "weight_and_balance_impact"
  | "parts_required"
  | "tooling_required"
  | "compliance"
  | "concurrent_requirements"
  | "consumables"
  | "manuals_publications_affected"

export const SB_DETAIL_FIELDS: { key: SbDetailKey; label: string }[] = [
  { key: "manpower_requirements", label: "Manpower requirements" },
  { key: "electrical_load_analysis", label: "Electrical load analysis" },
  { key: "weight_and_balance_impact", label: "Weight & balance impact" },
  { key: "parts_required", label: "Parts required" },
  { key: "tooling_required", label: "Tooling required" },
  { key: "compliance", label: "Compliance" },
  { key: "concurrent_requirements", label: "Concurrent requirements" },
  { key: "consumables", label: "Consumables" },
  {
    key: "manuals_publications_affected",
    label: "Manuals & publications affected",
  },
]
